import type React from 'react';
import { useLayoutEffect, useRef } from 'react';

/**
 * Focus handling for a non-modal panel opened from a trigger whose `aria-controls` names the panel.
 *
 * When the panel opens from its trigger, focus moves to `getFocusTarget()`, and again when `ready`
 * turns true if focus was left behind by a loading state. When the panel closes with focus inside it, or
 * inside a panel listed in `relatedPanelIds`, focus returns to the trigger. The return waits for the
 * commit to finish, so a trigger that was `inert` while the panel covered it can take focus again.
 */
export function usePanelFocus(
  panelId: string,
  getFocusTarget: () => HTMLElement | null,
  ready: boolean,
  relatedPanelIds: string[] = [],
) {
  const openerRef = useRef<HTMLElement | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: captures the opener once, at open
  useLayoutEffect(() => {
    const active = document.activeElement as HTMLElement | null;
    if (active?.getAttribute('aria-controls') === panelId) openerRef.current = active;
    return () => {
      const opener = openerRef.current;
      if (!opener) return;
      const focusInside = [panelId, ...relatedPanelIds].some((id) =>
        document.getElementById(id)?.contains(document.activeElement),
      );
      if (!focusInside) return;
      queueMicrotask(() => {
        const current = document.activeElement;
        const focusIsFree = !current || current === document.body;
        if (focusIsFree && opener.isConnected && !opener.closest('[inert]')) opener.focus();
      });
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refocuses only when loading finishes
  useLayoutEffect(() => {
    const opener = openerRef.current;
    if (!opener) return;
    const active = document.activeElement;
    // On open focus is still on the trigger; after loading it is left on the panel root or on <body>.
    const leftBehind = !active || active === document.body || active === document.getElementById(panelId);
    if (active === opener || leftBehind) getFocusTarget()?.focus();
  }, [ready]);
}

/** Closes the panel on Escape from its own controls, leaving Escape inside portaled menus to those menus. */
export function handlePanelEscape(e: React.KeyboardEvent<HTMLElement>, close: () => void) {
  if (e.key !== 'Escape' || e.defaultPrevented) return;
  if (!e.currentTarget.contains(e.target as Node)) return;
  e.preventDefault();
  e.stopPropagation();
  close();
}
